import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/SEOHead';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Calculator, 
  BookOpen, 
  Mail, 
  CheckSquare, 
  TrendingUp, 
  Sun, 
  PieChart, 
  BarChart3, 
  Wifi, 
  Zap,
  ArrowRight,
  ExternalLink
} from 'lucide-react';

export const ToolsPage = () => {
  const { t, i18n } = useTranslation();
  const isSpanish = i18n.language === 'es';
  const langPrefix = isSpanish ? '/es' : '/fr';

  const tools = [
    { 
      key: 'ebooks', 
      icon: BookOpen, 
      path: `${langPrefix}/ebooks`,
      color: 'from-emerald-500 to-emerald-600',
      badge: '5-10 guides',
      status: 'available'
    },
    { 
      key: 'templates', 
      icon: Mail, 
      path: `${langPrefix}/${isSpanish ? 'modelos-email' : 'modeles-emails'}`,
      color: 'from-amber-500 to-amber-600',
      badge: '16+ modèles',
      status: 'available'
    },
    { 
      key: 'moving', 
      icon: CheckSquare, 
      path: `${langPrefix}/${isSpanish ? 'mudanza' : 'demenagement'}`,
      color: 'from-indigo-500 to-indigo-600',
      badge: 'Checklist',
      status: 'available'
    },
    { 
      key: 'tracker', 
      icon: TrendingUp, 
      path: `${langPrefix}/${isSpanish ? 'seguimiento-precios' : 'suivi-prix'}`,
      color: 'from-rose-500 to-rose-600',
      badge: 'Temps réel',
      status: 'available'
    },
    { 
      key: 'solar', 
      icon: Sun, 
      path: `${langPrefix}/${isSpanish ? 'simulador-solar' : 'simulateur-solaire'}`,
      color: 'from-orange-500 to-orange-600',
      badge: 'Simulateur',
      status: 'available'
    },
    { 
      key: 'expenses', 
      icon: PieChart, 
      path: `${langPrefix}/${isSpanish ? 'seguimiento-gastos' : 'suivi-depenses'}`,
      color: 'from-teal-500 to-teal-600',
      badge: 'Suivi mensuel',
      status: 'available'
    },
    { 
      key: 'inflation', 
      icon: BarChart3, 
      path: `${langPrefix}/${isSpanish ? 'inflacion' : 'evolution-inflation'}`,
      color: 'from-purple-500 to-purple-600',
      badge: 'Données INSEE',
      status: 'available'
    },
    { 
      key: 'internet', 
      icon: Wifi, 
      path: `${langPrefix}/${isSpanish ? 'comparador-internet' : 'comparateur-internet'}`,
      color: 'from-cyan-500 to-cyan-600',
      badge: 'Comparateur',
      status: 'available'
    },
    { 
      key: 'electricity', 
      icon: Zap, 
      path: `${langPrefix}/${isSpanish ? 'luz' : 'comparateur-electricite'}`,
      color: 'from-yellow-500 to-yellow-600',
      badge: 'Fournisseurs',
      status: 'available'
    }
  ];

  const categories = [
    {
      title: t('tools.categories.guides.title'),
      description: t('tools.categories.guides.description'),
      tools: tools.filter(tool => ['ebooks', 'templates', 'moving'].includes(tool.key))
    },
    {
      title: t('tools.categories.analysis.title'),
      description: t('tools.categories.analysis.description'),
      tools: tools.filter(tool => ['tracker', 'expenses', 'inflation'].includes(tool.key))
    },
    {
      title: t('tools.categories.calculators.title'),
      description: t('tools.categories.calculators.description'),
      tools: tools.filter(tool => ['solar'].includes(tool.key))
    },
    {
      title: t('tools.categories.comparators.title'),
      description: t('tools.categories.comparators.description'),
      tools: tools.filter(tool => ['internet', 'electricity'].includes(tool.key))
    }
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'available':
        return <Badge className="bg-green-100 text-green-800 border-green-200">{t('status.available')}</Badge>;
      case 'coming_soon':
        return <Badge className="bg-orange-100 text-orange-800 border-orange-200">{t('status.coming_soon')}</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      <SEOHead type="tools" />
      
      {/* Hero Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 
              className="text-4xl lg:text-5xl font-bold text-sage-900 mb-4"
              data-testid="tools-page-title"
            >
              {t('tools.title')}
            </h1>
            <p className="text-xl text-sage-600 max-w-3xl mx-auto">
              {t('tools.description')}
            </p>
            
            {/* Lien Guide Anti-Inflation - FR uniquement */}
            {!isSpanish && (
              <div className="mt-8">
                <Card className="max-w-lg mx-auto bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200">
                  <CardContent className="p-6 text-center">
                    <div className="flex items-center justify-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-xl flex items-center justify-center">
                        <BookOpen className="w-5 h-5 text-white" />
                      </div>
                      <Badge className="bg-amber-100 text-amber-800">NOUVEAU</Badge>
                    </div>
                    <h3 className="font-bold text-amber-900 mb-2">🏆 Guide Anti-Inflation 2026</h3>
                    <p className="text-amber-700 text-sm mb-4">
                      Guide gratuit complet pour économiser 1500-3000€/an sans se priver
                    </p>
                    <Button 
                      asChild 
                      className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-semibold"
                    >
                      <Link to="/fr/guide-anti-inflation">
                        Consulter le Guide Gratuit
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Tools Categories */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {categories.map((category, categoryIndex) => (
            <div key={categoryIndex} className="mb-16">
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-sage-900 mb-2">
                  {category.title}
                </h2>
                <p className="text-sage-600">
                  {category.description}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {category.tools.map((tool) => (
                  <Card 
                    key={tool.key}
                    className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-0 shadow-lg overflow-hidden bg-white"
                    data-testid={`tool-card-${tool.key}`}
                  >
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${tool.color} flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                          <tool.icon className="w-7 h-7 text-white" />
                        </div>
                        <div className="flex gap-2">
                          <Badge variant="outline" className="text-xs">
                            {tool.badge}
                          </Badge>
                          {getStatusBadge(tool.status)}
                        </div>
                      </div>
                      
                      <CardTitle className="text-xl font-semibold text-sage-900">
                        {t(`tools.${tool.key}.title`)}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="pt-0">
                      <CardDescription className="text-sage-600 mb-4 leading-relaxed">
                        {t(`tools.${tool.key}.description`)}
                      </CardDescription>
                      
                      {tool.status === 'available' ? (
                        <Button 
                          asChild 
                          className="w-full justify-between group bg-sage-600 hover:bg-sage-700 transition-colors"
                        >
                          <Link to={tool.path}>
                            {t('actions.access_tool')}
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </Link>
                        </Button>
                      ) : (
                        <Button 
                          disabled
                          className="w-full opacity-50 cursor-not-allowed"
                        >
                          {t('actions.coming_soon')}
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-sage-900 mb-4">
            {t('actions.tool_not_suitable')}
          </h2>
          <p className="text-sage-600 text-lg mb-8">
            {t('actions.specific_needs')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild 
              className="bg-sage-600 hover:bg-sage-700 px-8 py-3"
            >
              <Link to="/contact">
                <Mail className="w-4 h-4 mr-2" />
                {t('actions.contact_us')}
              </Link>
            </Button>
            <Button 
              asChild 
              variant="outline" 
              className="border-2 border-sage-600 text-sage-600 hover:bg-sage-50 px-8 py-3"
            >
              <Link to="/about">
                {t('footer.learn_more')}
                <ExternalLink className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};